/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthConfig, ResetPasswordInfo } from '@shared/types/auth';
import { SignupUserData, User } from '@shared/types/common';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(private http: HttpClient) {}

  signup(userData: SignupUserData): Observable<void> {
    return this.http.post<void>('/api/v1/signup', userData);
  }

  signin(userData: SigninUserData): Observable<void> {
    return this.http.post<void>('/api/v1/signin', userData);
  }

  logout(): Observable<void> {
    return this.http.post<void>('/api/v1/logout', {});
  }

  getUser(): Observable<User> {
    return this.http.get<User>('/api/v1/user');
  }

  remindPassword(email: string): Observable<void> {
    return this.http.post<void>('api/v1/password/forgot', { email });
  }

  resetPassword(data: ResetPasswordInfo): Observable<void> {
    return this.http.post<void>('api/v1/password/reset', data);
  }

  updateUser(user: User): Observable<void> {
    return this.http.post<void>('api/v1/profile/save', user);
  }

  updatePassword(user: User, password: string): Observable<void> {
    return this.http.post<void>('api/v1/profile/save', { ...user, password });
  }

  resendActivation(email: string): Observable<void> {
    return this.http.post<void>('/api/v1/activation/resend', { email });
  }

  grantAdmin(token: string): Observable<void> {
    return this.http.post<void>(`/api/v1/grant-admin/${token}`, {});
  }

  getAuthConfig() {
    return this.http.get<AuthConfig>(`/api/v1/auth-config`);
  }
}

export interface SigninUserData {
  email: string;
  password: string;
  activationToken?: string;
}
